// [1.4.9] Tabelas de lookup e formatadores PUROS do chip do Modo Agente
// (US-13 — spec-002), extraídos de `run-status-chip.vue` pra manter o
// componente ≤ 150 linhas (regra do repo: arquivos pequenos de propósito).
// Nada aqui depende de Vue/props/estado de instância — só recebe primitivos
// e devolve string/objeto, o que também torna estas funções triviais de
// testar sem `shallowMount`.
import { formatNumber } from '@utils/Format'

// [VERIFY-1] `warning-new` NÃO é falso positivo — é injetada dinamicamente:
// `state/modules/parameters/state.js` declara `'warning-new': '#FF990A'` e
// a mutation `UPDATE_COLOR_MODE` (`mutations.js:55`) faz `document.
// documentElement.style.setProperty('--warning-new', ...)` (disparada no
// boot, `main.js`). O SASS estático (`_vars.sass`) não precisa emitir a
// var pra ela existir em runtime — mesmo mecanismo documentado em
// `ws/views/view-clients.vue:23`. Trocar por `warning` mudaria a cor real
// de #FF990A pra #FF6433 (`state.js`), sem ganho nenhum.
// [VERIFY-3b/G-D12] `queued`/`running`/`awaiting_approval` tinham a MESMA cor
// (warning-new) — ficavam graficamente idênticos (o ícone nem aparecia, ver
// `run-status-chip.vue`). `queued` NÃO usa `info` — o token não existe neste
// design system (só `--v-info-base` do Vuetify, não `--info`; usá-lo faria o
// ícone sumir em silêncio, mesmo bug documentado em `task-list-view.vue:536`)
// — usa `secondary` (neutro), a cor mais próxima de "aceito, ainda não
// executando".
// [VERIFY-3f] Correção sobre o `awaiting_approval: 'primary'` do VERIFY-3b:
// `primary` é WHITE-LABEL (`SET_COLORS`/`UPDATE_COLOR_MODE`, `mutations.js`,
// sobrescrito por escritório em `main.js:461`) — no escritório 83
// `--primary` (#128E45) e `--success` (#398E4A) são dois verdes quase
// idênticos, então "aguardando decisão" (awaiting_approval) e "concluído"
// (done) ficavam visualmente iguais; num escritório com `primary` laranja a
// mesma colisão migraria pra `running` (`warning-new`, #FF990A). Trocado
// para `warning` — token FIXO (`$warning` em `_vars.sass`, #FF6433, também
// no `$theme-colors` do SASS estático, então `var(--warning)` sempre existe,
// ao contrário de `info`), nunca sobrescrito por `SET_COLORS`, e distinto de
// `warning-new` (running) e `success` (done) em QUALQUER escritório.
// Semântica também bate: "precisa da sua ação" é mais parecido com atenção
// (warning) do que com identidade da marca (primary). Efeito colateral
// aproveitado: `expired`/`cancelled` (dois estados "terminou sem executar",
// nem sucesso nem erro do agente) passam a `secondary` (neutro) — antes
// `expired` usava `danger`, a MESMA cor de `failed` ("deu erro"), misturando
// duas semânticas diferentes; `failed` continua `danger`, exclusivo dele.
//
// ★ **[redesenho 19/09/2026] O ÍCONE SAIU, e o `label` virou caixa de frase.**
// A regra escrita pelo owner: *"O ponto de cor substitui o ícone + bolinha
// duplicados"*. Até aqui o chip desenhava DOIS sinais do mesmo estado lado a
// lado — um `icon-svg` explícito e a bolinha própria do `BaseStatus soft`,
// escondida por CSS. Hoje sobra a bolinha (que já nasce na `color` do estado,
// `.status-dot--soft` lê `--status-color`), e o `icon` deixou de existir na
// tabela: campo que ninguém lê é contrato falso para quem chega depois.
// ⚠️ Em `running` a bolinha cede o lugar a um giro — ver `ESTADO_EM_CURSO`.
//
// ⚠️ **`queued` diz "Na fila", não "aceito".** "Aceito" descrevia o que o
// SERVIDOR fez (admitiu o run); quem lê quer saber o que está acontecendo com
// ELE — e o que está acontecendo é espera. O auxiliar ao lado
// (`AUXILIAR_POR_ESTADO`) completa a frase com o motivo da espera. Registro do
// custo: enquanto não há worker no ambiente local, é NESTE estado que o run
// fica para sempre (ver §5.1 do CLAUDE.md), e o par rótulo+auxiliar é o que
// distingue "ninguém pegou ainda" de "quebrou".
export const ESTADOS = {
  queued: { label: 'Na fila', color: 'secondary' },
  running: { label: 'Executando', color: 'warning-new' },
  awaiting_approval: { label: 'Aguardando aprovação', color: 'warning' },
  done: { label: 'Concluído', color: 'success' },
  failed: { label: 'Falhou', color: 'danger' },
  cancelled: { label: 'Cancelado', color: 'secondary' },
  expired: { label: 'Expirado', color: 'secondary' },
}

/**
 * O estado em que a bolinha vira um giro.
 *
 * ⚠️ **É UM, e não dois.** O desenho do owner prevê também um `cancelling`
 * ("Cancelando", com giro), e ele **não existe no contrato**: a máquina de
 * estados do backend (`usecases/agente/estado.py`, espelhando o CHECK
 * `ck_intelligence_v2_runs_status` da migration v660) tem exatamente sete
 * estados — `queued`, `running`, `awaiting_approval`, `done`, `failed`,
 * `cancelled`, `expired` —, e o cancelamento não tem estado intermediário: o
 * run sai de `running`/`awaiting_approval` direto para `cancelled` quando o
 * `end` chega. O mesmo vale para o `accepted` do desenho: `queued` JÁ É "o
 * servidor aceitou", e um segundo rótulo para a mesma linha da tabela seria
 * um estado que nunca chega. Inventá-los aqui daria um chip que a máquina de
 * estados nunca produz — e o `validator` da prop `estado` recusaria o valor.
 * Se forem para existir, nascem no backend primeiro.
 *
 * ★ O que o consultor VÊ enquanto o cancelamento está em voo continua
 * resolvido sem estado novo: o "×" some no mesmo tick do clique
 * (`cancelamentoSolicitado`), e o chip segue em `running` até o `end`.
 */
export const ESTADO_EM_CURSO = 'running'

/**
 * Os estados em que o "×" aparece DENTRO da pílula.
 *
 * ⚠️ Regra do owner, e não uma inferência do `podeCancelar`: *"O ✕ mora dentro
 * da pílula, só em running/awaiting_approval"*. `podeCancelar` continua vindo
 * do pai (é ele que sabe se há run cancelável), mas nem todo run cancelável
 * merece o botão em qualquer estado — num terminal ele seria um gesto sem
 * efeito, e é o `&&` dos dois que o mantém fora de lá.
 */
export const ESTADOS_COM_CANCELAR = ['running', 'awaiting_approval']

/**
 * A frase auxiliar do estado, quando ela é FIXA.
 *
 * ⚠️ Só `queued` tem: os outros auxiliares são dado do run (tempo decorrido,
 * créditos, propostas, motivo da falha, efeitos já gravados) e vêm de
 * formatadores. Uma tabela com frases vazias para os outros seis convidaria a
 * preenchê-las com texto genérico onde hoje há informação.
 */
const AUXILIAR_POR_ESTADO = {
  queued: 'Aguardando um agente livre',
}

// [VERIFY-1/G-D4] `motivo` é "texto curto da MATRIZ DE FALHAS"
// (`contracts/ui-components.md` §2) — só faz sentido pro estado `failed`.
// Nos outros 6 estados, o valor que chega é bookkeeping interno da máquina
// de estados do backend (`estado.transicionar(motivo=...)`:
// `usecases/agente/aceitar_run.py`/`lease.py`) — ex.: `claim` (queued→running),
// `aguardando_decisao` (o worker suspendendo pra awaiting_approval),
// `fim_done` (fechamento do turno) — nunca um texto pra tela. Tabela cobre
// os motivos de FAILED conhecidos no backend hoje; desconhecido não
// aparece (a alternativa — mostrar o código cru — é o próprio bug do G-D4).
const MOTIVOS_DE_FALHA = {
  sem_claim: 'ninguém assumiu o turno a tempo',
  worker_morto_sem_end: 'o worker parou de responder',
  reserva_insuficiente_para_continuacao: 'saldo insuficiente para continuar',
  teto_chamadas: 'atingiu o teto de chamadas',
  teto_de_chamadas_atingido: 'atingiu o teto de chamadas',
}

export function resolverInfoEstado(estado) {
  return ESTADOS[estado] || ESTADOS.queued
}

/** A frase fixa deste estado, ou `''` quando o auxiliar dele é dado do run. */
export function auxiliarDoEstado(estado) {
  return AUXILIAR_POR_ESTADO[estado] || ''
}

/** O "×" cabe neste estado? (ver `ESTADOS_COM_CANCELAR`) */
export function permiteCancelar(estado) {
  return ESTADOS_COM_CANCELAR.indexOf(estado) !== -1
}

// [VERIFY-1/G-D4] Só traduz/mostra em `failed` — nos demais estados o
// `motivo` é bookkeeping interno (ver comentário de `MOTIVOS_DE_FALHA`).
// Código de falha desconhecido também não aparece (nunca o texto cru).
export function traduzirMotivo(estado, motivo) {
  if (estado !== 'failed') return ''
  return MOTIVOS_DE_FALHA[motivo] || ''
}

// [redesenho 19/09/2026] Sem os parênteses: no desenho do owner o contador é
// auxiliar de linha, irmão de "há 2 min" e "12,40 créditos" — e aqueles não
// vêm entre parênteses. Os parênteses vinham de quando o contador era a única
// coisa ao lado da pílula e precisava se descolar dela.
export function formatarContadorPropostas(quantidade) {
  const plural = quantidade > 1 ? 's' : ''
  return quantidade + ' proposta' + plural + ' aberta' + plural
}

// [VERIFY-2/G-D6a] Sem `ts` no contrato (`start`/`estado` em events.json não
// carregam timestamp — verificado; `run-atual`/`GET /runs/{id}` também não).
// Aproximação deliberada: `agoraMs` é o relógio LOCAL no instante em que o
// front observa `running` (evento ao vivo OU snapshot de reidratação) — não
// é o instante real do backend, mas é o único dado que existe sem mudar o
// contrato. `agoraMs` é parâmetro (não `Date.now()` interno) pra a função
// ficar pura e testável sem mockar o relógio global.
export function formatarTempoDecorrido(inicioRunningMs, agoraMs) {
  if (!inicioRunningMs) return ''
  const minutos = Math.floor((agoraMs - inicioRunningMs) / 60000)
  if (minutos < 1) return 'há poucos segundos'
  return 'há ' + minutos + ' min'
}

export function formatarCreditos(custoCreditos) {
  const plural = custoCreditos === 1 ? '' : 's'
  return formatNumber(custoCreditos) + ' crédito' + plural
}

// [VERIFY-3b/G-D17] `efeitos_conhecidos` (contrato `Recibo[]`, evento
// `estado`) — o consultor que cancela um run precisa ver o que JÁ teve
// efeito antes do cancelamento (US-6), não só "cancelado" no chip. Mesmas
// 3 entidades conhecidas de `ia-confirm-action.vue::ENTIDADE_LABELS` — as
// três são substantivos femininos em PT-BR, daí a tabela feminina; entidade
// DESCONHECIDA usa "registro" (masculino) + tabela masculina — nunca mistura
// gênero (ex.: "registro removida" seria erro de concordância).
const ENTIDADE_LABELS_EFEITO = {
  pm_task: 'tarefa',
  partners_oportunidades: 'oportunidade',
  crm_atividades_sugeridas_automatizadas: 'atividade do CRM',
}
const OPERACAO_PARTICIPIO_FEMININO = { create: 'criada', edit: 'editada', delete: 'removida' }
const OPERACAO_PARTICIPIO_MASCULINO = { create: 'criado', edit: 'editado', delete: 'removido' }

export function formatarEfeitosConhecidos(efeitos) {
  if (!efeitos || !efeitos.length) return ''
  const contagemPorChave = {}
  efeitos.forEach(function(efeito) {
    const chave = (efeito.entidade || '') + '|' + (efeito.operacao || '')
    contagemPorChave[chave] = (contagemPorChave[chave] || 0) + 1
  })
  const partes = Object.keys(contagemPorChave).map(function(chave) {
    const partesChave = chave.split('|')
    const entidade = partesChave[0]
    const operacao = partesChave[1]
    const quantidade = contagemPorChave[chave]
    const plural = quantidade > 1 ? 's' : ''
    const entidadeConhecida = Object.hasOwn(ENTIDADE_LABELS_EFEITO, entidade)
    const label = entidadeConhecida ? ENTIDADE_LABELS_EFEITO[entidade] : 'registro'
    const tabelaParticipio = entidadeConhecida ? OPERACAO_PARTICIPIO_FEMININO : OPERACAO_PARTICIPIO_MASCULINO
    const participioFallback = entidadeConhecida ? 'alterada' : 'alterado'
    const participio = tabelaParticipio[operacao] || participioFallback
    return quantidade + ' ' + label + plural + ' ' + participio + plural
  })
  // [redesenho 19/09/2026] "Já gravado:" e o ponto médio, conforme o desenho do
  // owner ("Já gravado: 2 tarefas criadas · 1 e-mail enviado"). "Antes de
  // cancelar" situava no TEMPO; "já gravado" diz o que importa — que aquilo
  // não volta atrás. O separador muda junto porque a vírgula, numa lista de
  // frases que já têm espaço interno, lê como continuação da frase anterior.
  return 'Já gravado: ' + partes.join(' · ')
}
